import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatCurrency = (value) =>
  `R$ ${Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (date) =>
  date.toLocaleDateString("pt-BR", {
    day:   "2-digit",
    month: "long",
    year:  "numeric",
  });

const generateOrcamentoNum = () => {
  const now = new Date();
  return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}-${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}`;
};

const isLightColor = (hex) => {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.55;
};

// ─── Style factory ────────────────────────────────────────────────────────────

const makeStyles = (color) => {
  const onColor     = isLightColor(color) ? "#111827" : "#FFFFFF";
  const onColorMuted = isLightColor(color) ? "#00000080" : "#FFFFFF99";

  return StyleSheet.create({
    page:      { padding: 0, fontSize: 10, fontFamily: "Helvetica", backgroundColor: "#FFFFFF" },
    pageInner: { flex: 1, flexDirection: "column" },

    // Header
    headerStrip: {
      backgroundColor: color,
      paddingHorizontal: 36, paddingVertical: 24,
      flexDirection: "row", alignItems: "center", justifyContent: "space-between",
    },
    logoBox:     { flex: 1, justifyContent: "center", alignItems: "flex-start" },
    logoImg:     { maxWidth: 130, maxHeight: 60, objectFit: "contain", objectPosition: "left center" },
    logoPlaceholder: { fontSize: 13, fontFamily: "Helvetica-Bold", color: onColor },
    headerRight: { alignItems: "flex-end", marginLeft: 16 },
    docTitle:    { fontSize: 20, fontFamily: "Helvetica-Bold", color: onColor },
    docMeta:     { fontSize: 9, color: onColorMuted, marginTop: 3 },

    // Body
    body: { paddingHorizontal: 36, paddingTop: 28, paddingBottom: 36, flex: 1 },

    // Empresa card
    clienteCard: {
      backgroundColor: "#F7FAFF",
      borderRadius: 8, borderWidth: 1, borderColor: "#D6E2F5",
      padding: 16, marginBottom: 16,
      flexDirection: "row", justifyContent: "space-between",
    },
    clienteSection:      { flex: 1 },
    clienteSectionTitle: { fontSize: 8, fontFamily: "Helvetica-Bold", color: "#8FA3BF", marginBottom: 6 },
    clienteNome:         { fontSize: 13, fontFamily: "Helvetica-Bold", color: "#111827", marginBottom: 4 },
    clienteInfo:         { fontSize: 9, color: "#4B5D7A", marginBottom: 2, lineHeight: 1.4 },
    divider:             { width: 1, backgroundColor: "#D6E2F5", marginHorizontal: 12 },

    // Sub-cliente (destinatário)
    destinatarioCard: {
      backgroundColor: "#EBF3FF",
      borderRadius: 8, borderWidth: 1, borderColor: "#B0C4E8",
      borderLeftWidth: 3, borderLeftColor: color,
      padding: 14, marginBottom: 20,
    },
    destinatarioTitle: { fontSize: 8, fontFamily: "Helvetica-Bold", color: color, marginBottom: 6 },
    destinatarioNome:  { fontSize: 12, fontFamily: "Helvetica-Bold", color: "#111827", marginBottom: 3 },
    destinatarioInfo:  { fontSize: 9, color: "#4B5D7A", marginBottom: 2, lineHeight: 1.4 },

    // Table
    tableTitle:    { fontSize: 8, fontFamily: "Helvetica-Bold", color: "#8FA3BF", marginBottom: 8 },
    table:         { borderRadius: 6, borderWidth: 1, borderColor: "#D6E2F5", marginBottom: 6 },
    tableHeader:   { flexDirection: "row", backgroundColor: color },
    tableRow:      { flexDirection: "row", borderTopWidth: 1, borderColor: "#D6E2F5" },
    tableRowEven:  { flexDirection: "row", borderTopWidth: 1, borderColor: "#D6E2F5", backgroundColor: "#F7FAFF" },
    tableEmpty:    { padding: 9, fontSize: 9.5, color: "#4B5D7A", flex: 1, textAlign: "center" },

    thProduto:  { padding: 9, fontSize: 8, fontFamily: "Helvetica-Bold", color: onColor, flex: 3 },
    thQtd:      { padding: 9, fontSize: 8, fontFamily: "Helvetica-Bold", color: onColor, width: 55, textAlign: "center" },
    thPreco:    { padding: 9, fontSize: 8, fontFamily: "Helvetica-Bold", color: onColor, width: 90, textAlign: "right" },
    thSubtotal: { padding: 9, fontSize: 8, fontFamily: "Helvetica-Bold", color: onColor, width: 90, textAlign: "right" },

    tdProduto:  { padding: 9, fontSize: 9.5, color: "#111827", flex: 3 },
    tdQtd:      { padding: 9, fontSize: 9.5, color: "#4B5D7A", width: 55, textAlign: "center" },
    tdPreco:    { padding: 9, fontSize: 9.5, color: "#4B5D7A", width: 90, textAlign: "right" },
    tdSubtotal: { padding: 9, fontSize: 9.5, color: "#111827", fontFamily: "Helvetica-Bold", width: 90, textAlign: "right" },

    // Total
    totalWrapper: { alignItems: "flex-end", marginBottom: 24 },
    totalBox:     {
      backgroundColor: "#EBF3FF", borderWidth: 1, borderColor: "#D6E2F5",
      borderRadius: 8, paddingVertical: 10, paddingHorizontal: 20,
      flexDirection: "row", alignItems: "center",
    },
    totalLabel: { fontSize: 9, fontFamily: "Helvetica-Bold", color: color, marginRight: 16 },
    totalValue: { fontSize: 16, fontFamily: "Helvetica-Bold", color: color },

    // Observações
    obsWrapper: {
      marginBottom: 24, backgroundColor: "#F7FAFF", borderRadius: 8,
      borderWidth: 1, borderColor: "#D6E2F5", borderLeftWidth: 3, borderLeftColor: color, padding: 14,
    },
    obsTitle: { fontSize: 8, fontFamily: "Helvetica-Bold", color: color, marginBottom: 5 },
    obsText:  { fontSize: 9.5, color: "#4B5D7A", lineHeight: 1.5 },

    // Assinaturas
    signatureRow: { flexDirection: "row", justifyContent: "space-between", marginBottom: 28 },
    signatureBox: {
      flex: 1, borderTopWidth: 1, borderColor: "#D6E2F5",
      paddingTop: 8, alignItems: "center", marginHorizontal: 12,
    },
    signatureLabel: { fontSize: 8, color: "#8FA3BF", textAlign: "center" },

    // Footer
    footer:      { borderTopWidth: 1, borderColor: "#D6E2F5", paddingTop: 12, flexDirection: "row", justifyContent: "space-between", alignItems: "center" },
    footerText:  { fontSize: 8, color: "#8FA3BF" },
    footerBrand: { fontSize: 8, fontFamily: "Helvetica-Bold", color: color },
  });
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * @param {object}  cliente        - dados da empresa (emitente)
 * @param {object|null} subCliente - destinatário do orçamento (opcional)
 * @param {string|null} logo       - base64 da logo
 * @param {Array}   produtos       - itens [ { nome, quantidade, preco } ]
 * @param {string}  observacoes
 * @param {string}  footerMessage
 * @param {string}  primaryColor
 */
const OrcamentoPDF = ({
  cliente,
  subCliente = null,
  logo,
  produtos,
  observacoes,
  footerMessage,
  primaryColor = "#1A56DB",
}) => {
  const s        = makeStyles(primaryColor);
  const hoje     = new Date();
  const total    = produtos.reduce((acc, p) => acc + p.preco * p.quantidade, 0);
  const orcNum   = generateOrcamentoNum();
  const validade = new Date(hoje);
  validade.setDate(validade.getDate() + 15);

  return (
    <Document title={`Orçamento - ${cliente.nome}`} author="FluxoEasy" creator="FluxoEasy">
      <Page size="A4" style={s.page}>
        <View style={s.pageInner}>

          {/* Header */}
          <View style={s.headerStrip}>
            <View style={s.logoBox}>
              {logo
                ? <Image src={logo} style={s.logoImg} />
                : <Text style={s.logoPlaceholder}>{cliente.nome ?? ""}</Text>}
            </View>
            <View style={s.headerRight}>
              <Text style={s.docTitle}>ORÇAMENTO</Text>
              <Text style={s.docMeta}>N. {orcNum}</Text>
              <Text style={s.docMeta}>Emitido em {formatDate(hoje)}</Text>
              <Text style={s.docMeta}>Válido até {formatDate(validade)}</Text>
            </View>
          </View>

          {/* Body */}
          <View style={s.body}>

            {/* Empresa (emitente) */}
            <View style={s.clienteCard}>
              <View style={s.clienteSection}>
                <Text style={s.clienteSectionTitle}>DADOS DA EMPRESA</Text>
                <Text style={s.clienteNome}>{cliente.nome}</Text>
                {!!cliente.cnpj_cpf && <Text style={s.clienteInfo}>CNPJ/CPF: {cliente.cnpj_cpf}</Text>}
                {!!cliente.endereco && <Text style={s.clienteInfo}>{cliente.endereco}</Text>}
              </View>
              <View style={s.divider} />
              <View style={s.clienteSection}>
                <Text style={s.clienteSectionTitle}>CONTATO</Text>
                {!!cliente.email    && <Text style={s.clienteInfo}>Email: {cliente.email}</Text>}
                {!!cliente.telefone && <Text style={s.clienteInfo}>Tel: {cliente.telefone}</Text>}
              </View>
            </View>

            {/* Destinatário (sub-cliente) — só exibe quando informado */}
            {!!subCliente && (
              <View style={s.destinatarioCard}>
                <Text style={s.destinatarioTitle}>ORÇAMENTO PARA</Text>
                <Text style={s.destinatarioNome}>{subCliente.nome}</Text>
                {!!subCliente.cpf_cnpj && <Text style={s.destinatarioInfo}>CPF/CNPJ: {subCliente.cpf_cnpj}</Text>}
                {!!subCliente.email    && <Text style={s.destinatarioInfo}>Email: {subCliente.email}</Text>}
                {!!subCliente.telefone && <Text style={s.destinatarioInfo}>Tel: {subCliente.telefone}</Text>}
                {!!subCliente.endereco && <Text style={s.destinatarioInfo}>{subCliente.endereco}</Text>}
              </View>
            )}

            {/* Tabela de itens */}
            <Text style={s.tableTitle}>ITENS DO ORÇAMENTO</Text>
            <View style={s.table}>
              <View style={s.tableHeader}>
                <Text style={s.thProduto}>PRODUTO / SERVIÇO</Text>
                <Text style={s.thQtd}>QTD.</Text>
                <Text style={s.thPreco}>PREÇO UNIT.</Text>
                <Text style={s.thSubtotal}>SUBTOTAL</Text>
              </View>

              {produtos.length === 0 && (
                <View style={s.tableRow}>
                  <Text style={s.tableEmpty}>Nenhum item adicionado</Text>
                </View>
              )}

              {produtos.map((p, i) => (
                <View key={`item-${i}-${p.nome}`} style={i % 2 !== 0 ? s.tableRowEven : s.tableRow}>
                  <Text style={s.tdProduto}>{p.nome}</Text>
                  <Text style={s.tdQtd}>{p.quantidade}</Text>
                  <Text style={s.tdPreco}>{formatCurrency(p.preco)}</Text>
                  <Text style={s.tdSubtotal}>{formatCurrency(p.preco * p.quantidade)}</Text>
                </View>
              ))}
            </View>

            {/* Total */}
            <View style={s.totalWrapper}>
              <View style={s.totalBox}>
                <Text style={s.totalLabel}>TOTAL GERAL</Text>
                <Text style={s.totalValue}>{formatCurrency(total)}</Text>
              </View>
            </View>

            {/* Observações */}
            {!!observacoes && (
              <View style={s.obsWrapper}>
                <Text style={s.obsTitle}>OBSERVAÇÕES</Text>
                <Text style={s.obsText}>{observacoes}</Text>
              </View>
            )}

            {/* Assinaturas */}
            <View style={s.signatureRow}>
              <View style={s.signatureBox}>
                <Text style={s.signatureLabel}>
                  {subCliente ? `Assinatura de ${subCliente.nome}` : "Assinatura do Cliente"}
                </Text>
              </View>
              <View style={s.signatureBox}>
                <Text style={s.signatureLabel}>Assinatura do Responsável</Text>
              </View>
            </View>

            {/* Footer */}
            <View style={s.footer}>
              <Text style={s.footerText}>
                {footerMessage || `Orçamento válido por 15 dias a partir de ${formatDate(hoje)}`}
              </Text>
              <Text style={s.footerBrand}>FluxoEasy</Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default OrcamentoPDF;